import _ from "lodash";
import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { MDBIcon, MDBCardBody } from "mdbreact";

import useModal from "hooks/useModal";
import { useAuth } from "hooks/useAuth";
import { fetchStudyProperties } from "actions/studies/studyPropertyActions";
import { fetchCustomProperties } from "actions/properties/customPropertyActions";

import Table from "components/common/tables/Table";
import Modal from "components/common/Modal";
import EmptyList from "components/common/EmptyList";
import SectionHeaderMenu from "components/nav/SectionHeaderMenu";
import StudyPropertyCreate from "./StudyPropertyCreate";
import StudyPropertyUpdate from "./StudyPropertyUpdate";
import StudyPropertyDelete from "./StudyPropertyDelete";

const StudyPropertyList = () => {
  // hooks
  const {
    modalOpen,
    headerText,
    setHeaderText,
    formId,
    footer,
    setFooter,
    actions,
    setActions,
    selected,
    setSelected,
    params,
    setParams,
    setModalState
  } = useModal();
  const dispatch = useDispatch();
  const auth = useAuth();

  // redux hooks
  const {
    propertiesAvailable: studyPropertiesAvailable,
    lastAction
  } = useSelector(state => state.studyProperties);

  const { propertiesAvailable } = useSelector(state => state.customProperties);

  // local state
  const [tableData, setTableData] = useState({
    columns: [
      {
        label: "#",
        field: "id",
        sort: "desc"
      },
      {
        label: "Name",
        field: "property.name",
        sort: "desc",
        minimal: "lg"
      },
      {
        label: "Type",
        field: "property.property_type.name",
        sort: "desc"
      },
      {
        label: "Required",
        field: "required",
        sort: "desc"
      },
      {
        label: "",
        field: "buttons",
        sort: "disabled",
        minimal: "lg"
      }
    ]
  });

  const toggleModal = useCallback(
    (e, type, item) => {
      if (e) e.preventDefault();
      if (type === "new") {
        setHeaderText("New Study Property");
        setFooter(true);
        setActions([
          {
            text: "Submit",
            color: "primary",
            additionalProps: {
              form: formId,
              type: "submit"
            }
          }
        ]);
      } else if (type === "edit") {
        setHeaderText("Edit Study Property");
        setFooter(true);
        setActions([
          {
            text: "Update",
            color: "primary",
            additionalProps: {
              form: formId,
              type: "submit"
            }
          }
        ]);
        setSelected(item);
      } else if (type === "delete") {
        setHeaderText("Delete Study Property");
        setFooter(true);
        setActions([
          {
            text: "Delete",
            color: "danger",
            additionalProps: {
              form: formId,
              type: "submit"
            }
          }
        ]);
        setSelected(item);
      }
      if (type) {
        setModalState(true);
      } else {
        setModalState(false);
      }
    },
    [formId, setActions, setFooter, setHeaderText, setModalState, setSelected]
  );

  // use effect setups
  useEffect(() => {
    dispatch(fetchStudyProperties());
    dispatch(fetchCustomProperties());
  }, [dispatch]);

  const sectionHeaderMenu = {
    order: 2,
    show: true,
    linkItems: [
      {
        name: (
          <>
            <MDBIcon icon="fas fa-plus" /> Add Study Property
          </>
        ),
        url: `/studies/properties`,
        exact: true,
        additionalProps: {
          active: true,
          onClick: e => toggleModal(e, "new"),
          className: "btn-sm"
        }
      }
    ]
  };

  const renderModalBody = () => {
    if (headerText === "New Study Property") {
      return (
        <StudyPropertyCreate
          formId={formId}
          params={params}
          setParams={setParams}
          closeModal={() => setModalState(false)}
        />
      );
    } else if (headerText === "Edit Study Property") {
      return (
        <StudyPropertyUpdate
          formId={formId}
          params={params}
          setParams={setParams}
          selected={selected}
          closeModal={() => setModalState(false)}
        />
      );
    } else if (headerText === "Delete Study Property") {
      return (
        <StudyPropertyDelete
          formId={formId}
          selected={selected}
          closeModal={() => setModalState(false)}
        />
      );
    }

    return "";
  };

  const renderTable = () => {
    if (_.size(studyPropertiesAvailable)) {
      return (
        <Table
          section="Property"
          tableData={tableData}
          setTableData={setTableData}
          rowData={studyPropertiesAvailable}
          sort={["id", "desc"]}
          pageLengthOpts={[50, 100, 150]}
          toggleModal={toggleModal}
          updatedState={lastAction}
          downloadableCSV
          editButton={auth?.user?.is_admin}
          deleteButton={auth?.user?.is_admin}
        />
      );
    } else if (_.isEmpty(propertiesAvailable)) {
      return (
        <EmptyList
          name="custom property"
          namePlural="custom properties"
          userRole={auth?.user?.user_level}
          customText="At least one custom property is required before making study properties. "
          createElement={<Link to="/admin/properties">Create</Link>}
        />
      );
    } else if (_.isEmpty(studyPropertiesAvailable)) {
      return (
        <EmptyList
          name="study property"
          namePlural="study properties"
          userRole={auth?.user?.user_level}
          createElement={
            <span className="link" onClick={e => toggleModal(e, "new")}>
              Create
            </span>
          }
        />
      );
    }

    return "";
  };

  return (
    <>
      <SectionHeaderMenu
        menuData={_.isEmpty(propertiesAvailable) || sectionHeaderMenu}
      />
      <MDBCardBody>{renderTable()}</MDBCardBody>
      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
        headerText={headerText}
        includeFooter={footer}
        actionButtons={actions}
      >
        {renderModalBody()}
      </Modal>
    </>
  );
};

export default StudyPropertyList;
